<template>
  <div class="controls">
    <div class="controls__start">
      <button
        id="training-session-details-back-btn"
        v-tooltip="'Navigate to training session manager'"
        class="button-light"
        aria-label="Back"
        @click="router.go(-1)"
      >
        <SVGIcon
          class="me-1"
          :iconName="'chevron_left'"
          :width="'18px'"
          :height="'18px'"
        />
        <span>Back</span>
      </button>
      <HelpTip>
        Select a tab below to see various training session details. To validate this model, click "VALIDATE".
        Click "COMPARE" to compare validation results.
        Note, that a training session may have multiple validation sessions with different parameters.
      </HelpTip>
      <HelpTip help_style="alert" v-if="cannotRunModelReason">
        Cannot invoke model runner: {{ cannotRunModelReason }}
      </HelpTip>
    </div>
    <div class="controls__end">
      <div class="controls__actions">
        <button v-tooltip="'Retrieve info, logs, charts for training session.'" class="button-light" @click="handleRefreshPage">
          <SVGIcon
            :iconName="'refresh'"
            :width="'18px'"
            :height="'18px'"
          />Refresh
        </button>
        <button
          v-if="permissions && permissions.trainer_write"
          v-tooltip="'Start validate session.'"
          class="button-light"
          @click="handleOpenStartValidatorModal"
        >
          <SVGIcon
            :iconName="'compare'"
            :width="'18px'"
            :height="'18px'"
          />Validate
        </button>
        <button v-tooltip="'Compare multiple validation results.'" class="button-light" @click="() => compareValidateSessionsModal.showModal()">
          <SVGIcon
            :iconName="'chart_multiple'"
            :width="'18px'"
            :height="'18px'"
          />Compare
        </button>
        <button v-if="canRunModel"  v-tooltip="'Run model interactively'" class="button-light" @click="() => gotoRunMode()">
          <SVGIcon
            :iconName="'play'"
            :width="'18px'"
            :height="'18px'"
          />Run Model
        </button>
        <button
          v-if="permissions && permissions.trainer_write"
          v-tooltip="`Terminate training but keep all logs and charts.`"
          class="button-light"
          @click="openConfirmStopTrainingSessionModal"
        >
          <SVGIcon
            :iconName="'stop_circle'"
            :width="'18px'"
            :height="'18px'"
          />
          Stop
        </button>
        <button
          v-if="permissions && permissions.trainer_write"
          v-tooltip="`Move training session to recycle bin. This will also move training artifacts and validate sessions to recycle bin.`"
          class="button-light delete"
          @click="openConfirmDeleteSessionModal"
        >
          <SVGIcon
            :iconName="'trash'"
            :width="'18px'"
            :height="'18px'"
          />
          Recycle Bin
        </button>
      </div>
    </div>
  </div>
  <div class="training-session scrollbar">
    <div class="session-data">
      <div class="tab-selector mb-1">
        <input
          id="option-info"
          v-model="tab"
          type="radio"
          name="tab-selector"
          :value="'info'"
        >
        <label for="option-info">Session</label>
        <input
          id="option-model-params"
          v-model="tab"
          type="radio"
          name="tab-selector"
          :value="'model-params'"
        >
        <label for="option-model-params">Parameters</label>
        <!-- <input
                id="option-metrics"
                v-model="tab"
                type="radio"
                name="tab-selector"
                :value="'metrics'"
              >
              <label for="option-metrics">Metrics</label> -->
        <input
          id="option-logs"
          v-model="tab"
          type="radio"
          name="tab-selector"
          :value="'logs'"
        >
        <label for="option-logs">Logs</label>

        <input
          id="option-charts"
          v-model="tab"
          type="radio"
          name="tab-selector"
          :value="'charts'"
        >
        <label for="option-charts">Charts</label>

        <input
          id="option-models"
          v-model="tab"
          type="radio"
          name="tab-selector"
          :value="'models'"
        >
        <label for="option-models">Artifacts</label>

        <input
          id="option-deploy"
          v-model="tab"
          type="radio"
          name="tab-selector"
          :value="'deploy'"
        >
        <label for="option-deploy">Deployment</label>

        <input
          id="option-converters"
          v-model="tab"
          type="radio"
          name="tab-selector"
          :value="'converters'"
        >
        <label for="option-converters">Converters</label>
      </div>
      <div v-if="tab==='info'" class="params">
        <div v-if="!loading" class="info">
          <div class="info__grid scrollbar">
            <div v-if="currentSession" class="info__grid-field session-row">
              <div class="description">Session</div>
              <div class="value">
                <div style="display: flex; gap: 12px; align-items: center;">
                  <div v-if="currentSession.id">
                    <EncodedIdField
                      :id="currentSession.id"
                      :type="'trainer'"
                      :hideLabel="true"
                    />
                  </div>
                  <span v-if="currentSession.name" style="flex: 1;">
                    {{ currentSession.name }}
                  </span>
                </div>
              </div>
            </div>
            <div v-if="currentSession && currentSession.description" class="info__grid-field description-row">
              <div class="description">Description</div>
              <div
                v-tooltip="currentSession.description ? currentSession.description : 'Unknown'"
                class="value"
              >
                {{ currentSession.description ? currentSession.description : "Unknown" }}
              </div>
            </div>
            <div v-if="user" class="info__grid-field created-by-row">
              <div class="description">Created By</div>
              <div class="value">
                <UserIcon
                  :user="user"
                  :size="'32px'"
                />
              </div>
              <span class="ml-2">{{ user ? user.first_name + ' ' + user.last_name : '' }}</span>
            </div>
            <div v-if="currentSession && !currentSessionStatus.toLowerCase().includes('complete')" class="info__grid-field current-stage-row">
              <div class="description">Current Stage</div>
              <TaskStatusBars
                :task="currentSession.docker_task"
                :singleBar="true"
                class="progress-bar"
              />
            </div>
            <div v-if="currentSession" class="info__grid-field status-row">
              <div class="description">Status</div>
              <div>
                <span v-if="currentSessionStatus.toLowerCase().includes('complete')" class="status-tag _complete"><div class="status-icon">&#x2713;</div>Complete</span>
                <span v-else-if="currentSessionStatus.toLowerCase().includes('error')" class="status-tag _error"><div class="status-icon">&#x2716;</div>Error</span>
                <span v-else-if="currentSessionStatus.toLowerCase().includes('terminate')" class="status-tag _warning"><div class="status-icon">&#x21;</div>Terminated</span>
                <span v-else-if="currentSessionStatus" class="status-tag _running"><div class="running-icon" />
                  {{ String(currentSessionStatus).charAt(0).toUpperCase() + String(currentSessionStatus).slice(1) }}
                </span>
              </div>
            </div>
            <div v-if="currentSession" class="info__grid-field model-type-row">
              <div class="description">Model Type</div>
              <div
                v-tooltip="currentSession.model ? currentSession.model : 'Unknown'"
                class="value"
              >
                {{ currentSession.model ? modelMap[currentSession.model] : 'Unknown' }}
              </div>
            </div>
            <template v-if="validateSessions && validateSessions.length > 0">
              <div v-for="validateSession, i in validateSessions" :key="validateSession.id" class="info__grid-field validate-sessions-row">
                <div class="description">{{ i === 0 ? "Validate Sessions" : '' }}</div>
                <div v-tooltip="`View details for ${validateSession.docker_task.name}`" class="value hyperlink" @click="handleValidateSessionClick(validateSession)">
                  <span v-if="validateSession.docker_task">{{ `(v-${Number(validateSession.id).toString(16)})` }} {{ validateSession.docker_task.name }}</span>
                </div>
              </div>
            </template>
            <div v-if="currentSession" class="info__grid-field dataset-row">
              <div class="description">Dataset</div>
              <div class="value">
                <div style="display: flex; gap: 12px; align-items: center;">
                  <div v-if="currentSession && currentSession.params.dataset_params && currentSession.params.dataset_params['dataset_id']">
                    <EncodedIdField
                      :id="currentSession.params.dataset_params['dataset_id']"
                      :type="'dataset'"
                      :hideLabel="true"
                    />
                  </div>
                  <div
                    v-if="currentSession && currentSession.params.trainer_params"
                    v-tooltip="currentSession.params.trainer_params ? `Open gallery for dataset ${currentSession.params.trainer_params['dataset_name']}` : 'Unknown'"
                    class="hyperlink"
                    @click="handleToGallery"
                    style="cursor: pointer;"
                  >
                    {{ currentSession.params.trainer_params ? currentSession.params.trainer_params['dataset_name'] : 'Unknown' }}
                  </div>
                  <span v-if="currentSession && currentSession.params.dataset_params && currentSession.params.dataset_params['tag_name']">
                    Tag: {{ currentSession.params.dataset_params['tag_name'] }}
                  </span>
                </div>
                <div v-if="currentSession && currentSession.params.dataset_params && currentSession.params.dataset_params['dataset_description']" style="margin-top: 6px; font-size: 0.85rem;">
                  {{ currentSession.params.dataset_params['dataset_description'] }}
                </div>
              </div>
            </div>
            <div v-if="currentSession && currentSession.params.dataset_params && currentSession.params.dataset_params['annotation_set_id']" class="info__grid-field annotation-set-id-row">
              <div class="description">Annotation Set</div>
              <div class="value">
                <div style="display: flex; gap: 12px; align-items: center;">
                  <div v-if="currentSession && currentSession.params.dataset_params && currentSession.params.dataset_params['annotation_set_id']">
                    <EncodedIdField
                      :id="currentSession.params.dataset_params['annotation_set_id']"
                      :type="'annotation set'"
                      :hideLabel="true"
                    />
                  </div>
                </div>
              </div>
            </div>
            <div v-if="currentSession && currentSession.params.dataset_params && currentSession.params.dataset_params['train_group_name']" class="info__grid-field train-group-row">
              <div class="description">Train Group</div>
              <div
                v-tooltip="currentSession.params.dataset_params ? currentSession.params.dataset_params['train_group_name'] : 'Unknown'"
                class="value"
              >
                {{ currentSession.params.dataset_params ? currentSession.params.dataset_params['train_group_name'] : 'Unknown' }}
              </div>
            </div>
            <div v-if="currentSession && currentSession.params.dataset_params && currentSession.params.dataset_params['val_group_name']" class="info__grid-field val-group-row">
              <div class="description">Val Group</div>
              <div
                v-tooltip="currentSession.params.dataset_params ? currentSession.params.dataset_params['val_group_name'] : 'Unknown'"
                class="value"
              >
                {{ currentSession.params.dataset_params ? currentSession.params.dataset_params['val_group_name'] : 'Unknown' }}
              </div>
            </div>
            <div v-if="currentSession" class="info__grid-field start-date-row">
              <div class="description">Start Date</div>
              <div class="value"><span v-if="currentSession.docker_task">{{ formatTime(currentSession.docker_task.date) }}</span></div>
            </div>
            <div v-if="currentSession" class="info__grid-field duration-row">
              <div class="description">Duration</div>
              <div class="value"><span v-if="currentSession.docker_task && getEndTime(currentSession.docker_task)" class="duration">{{ getEndTime(currentSession.docker_task) }}</span></div>
            </div>
          </div>
        </div>
        <div v-else id="loading-visualization"><div class="lds-ring"><div /><div /><div /><div /></div></div>
      </div>
      <div v-else-if="tab==='model-params'" class="params">
        <ShikiHighlight
          class="model-params scrollbar shiki-model-params"
          :code="model_parameters"
          lang="yaml"
          theme="vitesse-dark"
        />
      </div>

      <div v-else-if="tab==='metrics'" class="metrics">
        <div id="training-session-metrics" class="text-area-details-white scrollbar">
          <div
            v-for="(value, name, index) in metrics"
            :key="index"
            class="list-item"
            :title="value"
          >
            <label>{{ name }}</label>
            <span>{{ value }}</span>
          </div>
        </div>
      </div>
      <div v-else-if="tab==='logs'" class="system-logs">
        <div id="training-session-system-logs" class="text-area-box scrollbar">
          <RecycleScroller
            v-slot="{ item }"
            class="logs-scroller"
            :items="logLines"
            :item-size="20"
          >
            <div class="log-line">{{ item }}</div>
          </RecycleScroller>
        </div>
      </div>
      <div v-else-if="tab==='charts'" class="params">
        <TrainingCharts
          ref="trainingCharts"
          :trainerSession="currentSession"
          :projectID="currentProject.id"
        />
      </div>
      <div v-else-if="tab==='models'" class="artifacts scrollbar">
        <template v-if="artifacts && artifacts.length > 0">
          <DownloadModelCard
            v-for="(artifact, i) in artifacts"
            :key="i"
            :title="artifact.name"
            :modelType="artifact.modelType"
            class="download-model-card"
            :class="`${artifact.name}-btn`"
            @click="handleDownloadModel(artifact)"
          />
        </template>
        <template v-else>
          No model files
        </template>
      </div>

      <div v-else-if="tab==='deploy' && permissions && permissions.trainer_write" class="scrollbar">
        <!-- <template v-if="artifacts && artifacts.length > 0">   -->

        <div v-if="true">
          <div v-if="show_start_options" class="deploy-div">
            <div class="row mt-6">
              <div class="settings__control-group">
                <label class="required">Name</label>
                <input
                  id="deployment-name-input"
                  v-model="name"
                  class="select"
                  type="text"
                >
              </div>
            </div>

            <div class="row mt-3">
              <div class="settings__control-group">
                <label class="required">Version</label>
                <input
                  id="deployment-version-input"
                  v-model="Version"
                  class="select"
                  type="text"
                >
              </div>
            </div>

            <div class="row mt-3">
              <div class="settings__control-group">
                <label class="required">Author</label>
                <input
                  id="deployment-author-input"
                  v-model="Author"
                  class="select"
                  type="text"
                >
              </div>
            </div>

            <button
              id="deployment-submit-btn"
              class="button button-sm mt-4"
              style="align-self: flex-end;"
              @click="handleModelDeploy()"
            >
              Package Model for Deployment
            </button>
          </div>

          <div v-if="show_progress" class="deploy-div">
            Progress
            <TaskStatusBars :task="deploymentProgress" />
            <button class="button button-sm mt-4" style="align-self: flex-end;" @click="handleCancelModelDeploy()">Close</button>
          </div>
        </div>
        <div v-else>
          No Deployments
        </div>
      </div>
      <div v-if="tab==='converters'" class="params">
        <AppsMiniList type="converter" />
      </div>
    </div>
  </div>
  <ConfirmModal
    ref="confirmModal"
    :messageHeader="confirmMessageHeader"
    :message="confirmMessage"
    :buttonClass="'button-delete'"
    :buttonText="buttonText"
    @confirmed="confirmFunction"
  />
  <StartValidatorModal
    ref="startValidatorModal"
    :datasetId="modalDatasetId"
    :annotationSetId="modalAnnotationSetId"
    :trainSessionId="currentSession ? currentSession.id : null"
    @validate-session-started="handleValidateSessionCreated"
  />
  <CompareValidateSessionModal
    ref="compareValidateSessionsModal"
    :sessions="validateSessions"
    @open-compare="(sessionIds) => handleOpenCompareSessionsPage(sessionIds)"
  />
</template>

<script setup>
import {
  ref, computed, onBeforeUnmount, onMounted, watch, inject,
} from 'vue';
import { useRouter, useRoute } from 'vue-router';
import DatastoreConnect from '@/assets/js/DatastoreFunctions/datastore-interface';
import * as YAML from 'yaml';
import { useTimeoutPoll } from '@vueuse/core';
import { useToast } from 'vue-toast-notification';
import 'vue-toast-notification/dist/theme-sugar.css';
import ShikiHighlight from "@/components/ShikiHighlight.vue";
import { RecycleScroller } from 'vue-virtual-scroller';
import 'vue-virtual-scroller/dist/vue-virtual-scroller.css';
import { useStore } from 'vuex';
import EncodedIdField from '@/components/EncodedIdField.vue';
import UserIcon from '@/components/UserIcon.vue';
import TrainingCharts from '@/components/TrainerComponent/TrainerExperimentAndSessions/TrainingCharts.vue';
import DownloadModelCard from '@/components/TrainerComponent/TrainerExperimentAndSessions/DownloadModelCard.vue';
import SVGIcon from '@/components/SVGIcon.vue';
import ConfirmModal from '@/components/ConfirmModal.vue';
import TaskStatusBars from '@/components/TaskStatusBars.vue';
import StartValidatorModal from '@/components/ValidationPage/StartValidatorModal.vue';
import CompareValidateSessionModal from '@/components/TrainerComponent/TrainerExperimentAndSessions/CompareValidateSessionModal.vue';
import HelpTip from '../../HelpTip.vue';
import AppsMiniList from '@/components/Apps/AppsMiniList.vue';
// Refs
const isPublic = inject('isPublic', false);
const store = useStore();
const tab = ref('info');
const logs = ref(null);
const artifacts = ref([]);
const logLines = ref([]);
const $toast = useToast();
const route = useRoute();
const router = useRouter();
const confirmMessage = ref(null);
const confirmMessageHeader = ref(null);
const toDelete = ref(null);
const confirmFunction = ref(null);
const buttonText = ref('Delete');
const confirmModal = ref(null);
const validateSessions = ref(null);
const hideProgress = ref(false);
const loading = ref(false);
const startValidatorModal = ref(null);
const modalDatasetId = ref(0);
const modalAnnotationSetId = ref(0);
const compareValidateSessionsModal = ref(null);
const trainingCharts = ref(null);
const permissions = ref(null);
const cannotRunModelReason = ref("");

const show_start_options = ref(true);
const show_progress = ref(false);

let deployUpdateTime = null;

defineProps({
  projectId: {
    type: [String, Number],
    required: true,
  },
})

const deploymentProgress = ref({
  "status": "Running",
  "docker_status": "",
  "message": "Initializing...",
  "error_messages": null,
  "date": "2025-03-26T03:49:08.767694Z",
  "end_date": "2025-03-26T04:29:29.305341Z",
  "data": {
    "stage_definitions": [
      {
        "convert": "Model Conversion",
      },
      {
        "package": "Packaging Model",
      },
    ],
    "stages": {
      "convert": {
        "label": "Caching group: train",
        "percentage": 0,
        "stage": "cache-train",
        "status": "Running",
      },
      "package": {
        "label": "Caching group: val",
        "percentage": 0,
        "stage": "cache-val",
        "status": "Running",
      },
    },
  },
  "count": 0,
  "total": 0,
});

const currentSession = computed({
  get() {
    return store.state.experiments.currentSession;
  },
  set(session) {
    // Note: we are using destructuring assignment syntax here.
    store.commit('experiments/setCurrentSession', session);
  },
});
const currentSessionStatus = computed(() => currentSession.value?.docker_task?.status);

const userList = computed(() => store.state.user.userList);
const user = computed(() => userList.value?.find((e) => e.username === currentSession.value?.docker_task.username));

const currentProject = computed(() => store.state.projects.currentProject);

const currentExperiment = computed(() => store.state.experiments.currentExperiment);

const modelMap = ref({
  "modelpack": "ModelPack",
  "fusion": "EdgeFirst Fusion",
});

const canRunModel = computed(() => {

  let onnxFile="";
  let labelsFile="";
  for (const file of artifacts.value){
    if (file.name.indexOf('.onnx')>=0){
      onnxFile=file.name;
    }
    if (file.name.indexOf('labels.txt')>=0){
      labelsFile=file.name;
    }
  }
  if (onnxFile=="") {
    cannotRunModelReason.value="Missing ONNX artifacts file";
    return false;
  }
  if (labelsFile=="") {
    cannotRunModelReason.value="Missing labels.txt artifacts file";
    return false;
  }

  // if (currentSession.value?.model!='modelpack') {
  //   cannotRunModelReason.value="Only 'Modelpack' runner is supported";
  //   return false;
  // }

  cannotRunModelReason.value="";
  return true;

});


const metrics = computed(() => {
  const result = {};
  for (const [key, value] of Object.entries(currentSession.value?.metrics || {})) {
    if (key !== 'timings' && key !== 'model' && key !== 'dataset'
        && key !== 'class_histogram_data' && key !== 'confusion_matrix_data' && key !== 'precision_recall_data') {
      result[key] = value;
    }
  }
  return result;
});

const model_parameters = computed(() => YAML.stringify(currentSession.value?.params?.model_params || {}, { indent: 8 }));

// Lifecycle hooks

const getLogs = async () => {
  if (currentSession.value && currentSession.value.id) {
    const dataConnect = new DatastoreConnect();
    const resp = await dataConnect.getTrainingSessionLogs({
      session_id: currentSession.value.id,
    });

    let temp = "";
    if (!resp || resp.error || !resp.result) {
      temp += `No logs file to display`;
    } else {
      temp += resp.result;
    }

    logs.value = temp;
  } else {
    logs.value = null;
  }

  if (logs.value) {
    logLines.value = logs.value.split('\n');
  }
};

const { isActive: isGetLogsActive, pause: pauseGetLogs, resume: resumeGetLogs } = useTimeoutPoll(getLogs, 5000);

const handleDownloadModel = (model) => {
  const url = `/download_model?training_session_id=${currentSession.value.id}&file=${model.name}`;

  const a = document.createElement('a');
  a.href = url;
  a.setAttribute('download', ''); // This tells the browser to download instead of navigate

  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
};

const listTrainingSessionArtifacts = async () => {
  const dataConnect = new DatastoreConnect();
  return dataConnect.getTrainingSessionArtifacts({
    training_session_id: currentSession.value.id,
  })
    .then((resp) => {
      if (resp.error !== undefined) {
        throw Error(resp.error.message);
      }
      return resp.result;
    })
    .catch((error) => {
      throw error;
    });
};

const gotoRunMode = () => {
  
  //get onnx file
  let filename="";
  for (const file of artifacts.value){
    if (file.name.indexOf('.onnx')>0){
      filename=file.name;
      break;
    }
  }

  if (filename=="") {
        $toast.open({
            queue: true,
            dismissible: true,
            message: `No Model file of type ONNX`,
            type: "error",
            position: "top",
          });
        return;
  }
  router.push({ name: "model_runner", query: { trainer: currentSession.value.id,model: filename } });
};

const handleModelDeploy = () => {
  show_progress.value = true;
  show_start_options.value = false;
  let modelConvertProgress = 0;
  let modelDeployProgress = 0;
  deployUpdateTime = setInterval(() => {
    modelConvertProgress++;
    if (modelConvertProgress > 100) modelConvertProgress = 100;
    deploymentProgress.value.data.stages['convert'].percentage = modelConvertProgress;

    if (modelConvertProgress === 100) {
      modelDeployProgress++;
      if (modelDeployProgress > 100) modelDeployProgress = 100;
      deploymentProgress.value.data.stages['package'].percentage = modelDeployProgress;
    }
  }, 300);
};
const handleCancelModelDeploy = () => {
  show_progress.value = false;
  show_start_options.value = true;
  clearInterval(deployUpdateTime);
  deployUpdateTime = null;
  deploymentProgress.value.data.stages['convert'].percentage = 1;
  deploymentProgress.value.data.stages['package'].percentage = 0;
};

const handleRefreshPage = () => {
  if (tab.value === "charts") {
    trainingCharts.value.getChartsData();
  } else if (tab.value === "logs") {
    getLogs();
  }
  handleGetSession();
};

const handleGetSession = async (session_id) => {
  if (!session_id || currentSession.value.id) return;
  const dataConnect = new DatastoreConnect();
  await dataConnect.getTrainingSession({ trainer_session_id: session_id || currentSession.value.id })
    .then(async (data) => {
      if (data.result) {
        const date1 = new Date(data.result.deleted);
        const date2 = new Date("0001-01-01T00:00:00Z");
        if (date1 > date2) {
          $toast.open({
            queue: true,
            dismissible: true,
            message: `Failed to retrieve training session: Session has been removed`,
            type: "error",
            position: "top",
          });
          router.push({ name: `${isPublic ? 'public.' : ''}experiment.training.list`, query: { exp_id: data.result.trainer_id } });
        } else {
          currentSession.value = data.result;
        }
      } else if (data.error) {
        $toast.open({
          queue: true,
          dismissible: true,
          message: `Failed to retrieve training session: ${data.error.message}`,
          type: "error",
          position: "top",
        });
      }
    })
    .catch((error) => {
      $toast.open({
        queue: true,
        dismissible: true,
        message: `Failed to retrieve training session: ${error}`,
        type: "error",
        position: "top",
      });
    });
};

const { isActive: isGetSessionsActive, pause: pauseGetSession, resume: resumeGetSession } = useTimeoutPoll(handleGetSession, 30000);

const getEndTime = (docker_task) => {
  if (docker_task.end_date !== "0001-01-01T00:00:00Z" && docker_task.date !== "0001-01-01T00:00:00Z") {
    const msDiff = new Date(docker_task.end_date) - new Date(docker_task.date);
    const minutes = Math.floor(msDiff / (1000 * 60)) % 60;
    const hours = Math.floor(msDiff / (1000 * 60 * 60)) % 24;
    const days = Math.floor(msDiff / (1000 * 60 * 60 * 24));
    const result = [];
    if (days > 0) result.push(`${days} day${days > 1 ? 's' : ''}`);
    if (hours > 0) result.push(`${hours} hr${hours > 1 ? 's' : ''}`);
    if (minutes > 0) result.push(`${minutes} min${minutes > 1 ? 's' : ''}`);
    return result.join(" ") || "0 mins";
  } else if (docker_task.end_date === "0001-01-01T00:00:00Z" && docker_task.date !== "0001-01-01T00:00:00Z") {
    const msDiff = Date.now() - new Date(docker_task.date);
    const minutes = Math.floor(msDiff / (1000 * 60)) % 60;
    const hours = Math.floor(msDiff / (1000 * 60 * 60)) % 24;
    const days = Math.floor(msDiff / (1000 * 60 * 60 * 24));
    const result = [];
    if (days > 0) result.push(`${days} day${days > 1 ? 's' : ''}`);
    if (hours > 0) result.push(`${hours} hr${hours > 1 ? 's' : ''}`);
    if (minutes > 0) result.push(`${minutes} min${minutes > 1 ? 's' : ''}`);
    return result.join(" ") || "0 mins";
  } else {
    return null;
  }
};

const formatTime = (str) => `${new Date(str).toLocaleTimeString('default', {
  year: 'numeric', month: 'short', day: 'numeric', hour: 'numeric', minute: 'numeric', hour12: true,
})}`;

const handleToGallery = () => {
  if (currentSession.value.params.dataset_params['dataset_id']) {
    router.push({ name: `${isPublic ? 'public.' : ''}datasets.gallery.main`, query: { dataset: currentSession.value.params.dataset_params['dataset_id'] } });
  }
};

const openConfirmStopTrainingSessionModal = () => {
  if (currentSession.value) {
    confirmMessage.value = `Are you sure you want to stop training session "${currentSession.value.name}"?`;
    confirmMessageHeader.value = 'Stop Training Session';
    buttonText.value = 'Stop';
    confirmModal.value.showModal();
    confirmFunction.value = () => {
      handleStopTrainingSessions([currentSession.value.id]);
    };
  }
};
const openConfirmDeleteSessionModal = () => {
  if (currentSession.value) {
    confirmMessage.value = `Are you sure you want to move training session "${currentSession.value.name}" to recycle bin? This will also move training artifacts and validate sessions to recycle bin.`;
    confirmMessageHeader.value = 'Remove Training Session';
    buttonText.value = 'Remove';
    confirmModal.value.showModal();
    confirmFunction.value = () => {
      router.push({ name: 'experiment.training', query: { exp_id: currentSession.value.trainer_id } });
      handleDeleteSession(currentSession.value.id);
    };
  }
};

const handleStopTrainingSessions = async (ids) => {
  const dataConnect = new DatastoreConnect();
  await dataConnect.terminateTrainingSessions({
    session_ids: ids,
  })
    .then((data) => {
      handleGetSession();
      if (data.error) {
        $toast.open({
          queue: true,
          dismissible: true,
          message: `Failed to stop training session: ${data.error.message}`,
          type: "error",
          position: "top",
        });
      }
    })
    .catch((error) => {
      $toast.open({
        queue: true,
        dismissible: true,
        message: `Failed to stop training session: ${error}`,
        type: "error",
        position: "top",
      });
    });

  toDelete.value = null;
};
const handleDeleteSession = async (id) => {
  const dataConnect = new DatastoreConnect();
  await dataConnect.deleteTrainingSession({
    session_id: id,
  })
    .then(async (data) => {
      if (data.error) {
        $toast.open({
          queue: true,
          dismissible: true,
          message: `Failed to delete training session: ${data.error.message}`,
          type: "error",
          position: "top",
        });
      }
    })
    .catch((error) => {
      $toast.open({
        queue: true,
        dismissible: true,
        message: `Failed to delete training session: ${error}`,
        type: "error",
        position: "top",
      });
    });

  toDelete.value = null;
};

const getValidateSessionListByTrainingSession = async (id) => {
  const dataConnect = new DatastoreConnect();
  await dataConnect.getValidateSessionListByTrainingSession({
    training_session_id: id,
  })
    .then(async (data) => {
      if (data.error) {
        $toast.open({
          queue: true,
          dismissible: true,
          message: `Failed to retrieve validate sessions: ${data.error.message}`,
          type: "error",
          position: "top",
        });
      } else if (data.result) {
        validateSessions.value = data.result;
      }
    })
    .catch((error) => {
      $toast.open({
        queue: true,
        dismissible: true,
        message: `Failed to retrieve validate sessions: ${error}`,
        type: "error",
        position: "top",
      });
    });

  toDelete.value = null;
};

async function getProjectAccessForSingleUser() {
  if (currentProject.value && store.state.user?.user?.username) {
    const dataConnect = new DatastoreConnect();
    await dataConnect.getProjectAccessForSingleUser({
      project_id: currentProject.value.id,
      username: store.state.user.user.username,
    })
      .then((data) => {
        if (data.result) {
          permissions.value = data.result;
        } else if (data.error) {
          $toast.open({
            queue: true,
            dismissible: true,
            message: `Failed to retrieve project access: ${data.error.message}`,
            type: "error",
            position: "top",
          });
        }
      })
      .catch((e) => {
        $toast.open({
          queue: true,
          dismissible: true,
          message: `Failed to retrieve project access: ${e}`,
          type: "error",
          position: "top",
        });
      });
  }
}

const handleValidateSessionClick = (validateSession) => {
  router.push({ name: `${isPublic ? 'public.' : ''}experiment.validation.details`, query: { val_session_id: validateSession.id } });
};

const handleValidateSessionCreated = async () => {
  await getValidateSessionListByTrainingSession(currentSession.value.id);
};

const handleOpenStartValidatorModal = () => {
  if (currentSession.value && currentSession.value.params.dataset_params) {
    modalDatasetId.value = currentSession.value.params.dataset_params['dataset_id'];
    modalAnnotationSetId.value = currentSession.value.params.dataset_params['annotation_set_id'];
  }
  startValidatorModal.value.showModal();
};

const handleOpenCompareSessionsPage = (sessionIds) => {
  router.push({ name: `${isPublic ? 'public.' : ''}experiment.validation.compare`, query: { exp_id: currentExperiment.value.id, session_ids: encodeURI(JSON.stringify(sessionIds)) } });
};

watch(tab, () => {
  router.replace({ name: route.name, query: { train_session_id: currentSession.value.id, mode: tab.value } });

  if (tab.value === 'logs') {
    resumeGetLogs();
  } else {
    pauseGetLogs();
  }

  if (tab.value === 'info') {
    resumeGetSession();
  } else {
    pauseGetSession();
  }
});

onBeforeUnmount(() => {
  pauseGetLogs();
  pauseGetSession();
});

onMounted(async () => {
  loading.value = true;
  deploymentProgress.value.data.stages['convert'].percentage = 1;
  const mode = route.query.mode;
  if (mode) {
    tab.value = mode;
  }

  // Handle both numeric ID and hex UID format (t-hexvalue)
  const sessionIdParam = route.query.train_session_id;
  let session_id = null;
  
  if (sessionIdParam) {
    if (sessionIdParam.startsWith('t-')) {
      // Convert hex format (t-7bf) to decimal
      session_id = parseInt(sessionIdParam.substring(2), 16);
    } else {
      // Try to parse as numeric, otherwise use as-is
      const parsedId = parseInt(sessionIdParam);
      session_id = isNaN(parsedId) ? sessionIdParam : parsedId;
    }
  }

  if (!session_id && currentSession.value) {
    session_id = currentSession.value.id;
    router.replace({ name: route.name, query: { train_session_id: session_id, mode } });
    await handleGetSession();
  }

  await getValidateSessionListByTrainingSession(session_id);

  getProjectAccessForSingleUser();

  if (tab.value === 'info') {
    resumeGetSession();
  }

  artifacts.value = await listTrainingSessionArtifacts()
    .catch((err) => {
      // commenting this since no artifacts is not an error
      // $toast.open({
      //   queue: true,
      //   duration: 5000,
      //   message: `Error occured while fetching artifacts`,
      //   type: 'error',
      //   position: 'top',
      // });
    });

  loading.value = false;
});

</script>

<style lang="scss" scoped>

.deploy-div {
  display: flex;
  flex-direction: column;
  min-width: 200px;
  max-width: 400px;
  margin: 10px;
}

.controls {
  display: flex;
  margin: 20px 20px 0 20px;
  align-items: flex-start;
  gap: 10px;

  &__count {
    font-weight: bold;
    font-size: 1rem;
  }

  &__start {
    display: flex;
    justify-content: flex-start;
    width: fit-content;
    align-items: flex-start;
    gap: 10px;
  }

  &__end {
    display: flex;
    justify-content: flex-end;
    flex: 1;
  }

  &__actions {
    display: flex;
    flex-direction: row;
    align-items: center;
    flex-wrap: wrap;
    gap: 12px;
    height: 100%;
  }
}

@media (max-width: $breakpoint-sm) {
  .controls {
    flex-direction: column;
    &__start{
      display: flex;
      align-items: flex-start;
      flex-direction: column;
      width: 100%;
    }

    &__middle{
      display: flex;
      align-items: flex-start;
      flex-direction: column;
      width: 100%;
    }

    &__end{
      display: flex;
      align-items: flex-end;
      flex-direction: column;
      width: 100%;
    }
  }
}

.training-session{
  display: flex;
  flex-direction: row;
  height: 100%;
  width: 100%;
  overflow-x: auto;
}

.session-data {
  display: flex;
  flex-direction: column;
  flex: 1 1 auto;
  height: 100%;
  width: calc(100% - 400px);
  padding: 16px;

  ul {
    list-style-type: none;
  }

  h3 {
    margin: 0;
    margin-bottom: 10px;
    font-size: 1rem;
    text-align: left;
    @include themify() {
      color: themed('body-text-color-secondary');
    }
  }

  .text-area-box {
    display: flex;
    flex-direction: row;
    border-radius: 4px;
    background: #121212;
    border: 1px solid grey;
    height: 100%;
    // padding: 15px;
    overflow-y: auto;

    span {
      color: white;
      font-weight: 700;
      font-size: 0.9rem;

      pre {
        font-family: 'Courier New', monospace !important;
        text-align: left;
      }
    }
  }

  .text-area-details-white {
    display: grid;
    grid-template-columns: repeat(auto-fit, 450px);
    grid-auto-rows: 50px;
    grid-gap: 20px 5px;
    height: 100%;
    border-radius: 4px;
    background: var(--color-white-100);
    border: 1px solid var(--color-primary-100);
    padding: 15px;
    overflow-y: auto;

    .list-item {
      height: fit-content;
      display: flex;
      flex-direction: column;
      text-align: left;
      padding: 5px;

      label {
        @include themify() {
          color: tint-color(themed('body-text-color-secondary'), 20%);
        }
        font-weight: 700;
        font-size: 0.9rem;
        overflow: hidden;
        white-space: nowrap;
        text-overflow: ellipsis;
      }

      span {
        @include themify() {
          color: themed('body-text-color');
        }
        font-weight: 600;
        font-size: 0.9rem;
        overflow: hidden;
        white-space: nowrap;
        text-overflow: ellipsis;
      }
    }
  }

  .system-logs, .metrics, .params {
    display: flex;
    flex-direction: column;
    height: 100%;
    overflow-y: auto;
  }
}

.model-params {
  display: flex;
  flex-direction: column;
  height: 100%;
  border-radius: 4px;
  background: var(--color-white-100);
  border: 1px solid grey;
  overflow-y: auto;
}

.tab-selector {
  display: flex;
  flex-direction: row;
}

.tab-selector input[type="radio"] {
  display: none;
}

.tab-selector label {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 0 12px;
  height: 32px;
  width: fit-content;
  font-size: 0.9rem;
  font-weight: bold;
  color: var(--body-text-color-heading);
  cursor: pointer;

  &:hover {
    background: rgba(0, 0, 0, 0.1);
  }
}

.tab-selector input[type="radio"]:checked+label {
  @include themify() {
    color: themed('color-primary-500');
    border-bottom: 2px solid themed('color-primary-500');
  }
}

.artifacts {
  display: flex;
  flex-direction: column;
  gap: 8px;
  overflow-y: auto;
  min-height: 0;
  padding: 4px;
  max-width: 400px;
}

.download-model-card {
  cursor: pointer;
  &:hover {
    box-shadow: 0px 3px 4px rgba(0, 0, 0, 0.375), 0 0 2px 2px rgba(0, 0, 0, 0.125);
  }
}

.shiki-model-params {
  text-align: left;
  white-space: pre-wrap;
}

:deep(.shiki-model-params pre) {
  padding: 16px;
  flex: 1 1 auto;
  overflow: auto;
}

.logs-scroller {
  height: 100%;
  width: 100%;
}

.log-line {
  text-align: left;
  white-space: pre;
  color: white;
}

:deep(.vue-recycle-scroller__item-wrapper) {
  overflow: visible !important;
}

:deep(.vue-recycle-scroller) {
  overflow: auto !important;
  padding: 15px;
}

.info {
  position: relative;
  display: flex;
  flex-direction: column;
  flex: 1 1 auto;
  min-height: 0;
  padding: 16px;
  overflow: auto;
  border-radius: 4px;
  margin: 1px 1px;
  box-shadow: 1px 1px 4px 1px rgba(0, 0, 0, 0.2), 0 0 1px 1px rgba(0, 0, 0, 0.05);

  @include themify() {
    background: themed('card-color');
  }

  &__header{
    display: flex;
    flex-direction: column;
    align-items: flex-start;
    gap: 10px;

  }

  &__grid-field {
    display: flex;
    flex-direction: row;
    align-items: center;
    margin-top: 2px;
  }

  &__grid {
    display: grid;
    overflow-y: auto;

    .description {
      font-size: 0.95rem;
      font-weight: 600;
      height: 32px;
      color: var(--body-text-color);
      width: 250px;
    }
    .value {
      font-size: 0.95rem;
      overflow: hidden;
      white-space: nowrap;
      text-overflow: ellipsis;
    }

    .progress-bar {
      max-width: 400px;
      min-width: 100px;
    }
  }

  &._separator::after {
    content: "";
    position: absolute;
    right: 0;
    top: 5%;
    bottom: 5%;
    width: 1px;
    background-color: #9a9ca2;
  }
}

.status-tag {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 100px;
  min-width: 100px;
  border-radius: 18px;
  padding: 4px 6px;
  line-height: 1;
  font-size: 0.625rem;
  font-weight: 700;
  white-space: nowrap;
  border: 2px solid;
  box-shadow: 0px 0px 1px rgb(0 0 0 / 25%), 0px 1px 4px rgb(0 0 0 / 25%);
  @include themify() {
    color: themed('color-black');
  }

  &._complete {
    @include themify() {
      color: themed('color-status-complete');
      background: themed('color-status-complete-background');
    }
  }
  &._running {
    @include themify() {
      color: themed('color-status-running');
      background: themed('color-status-running-background');
    }
  }
  &._error {
    @include themify() {
      color: themed('color-status-error');
      background: themed('color-status-error-background');
    }
  }

  &._warning {
    @include themify() {
      color: themed('color-status-warning');
      background: themed('color-status-warning-background');
    }
  }

  .running-icon {
    color: inherit;
    width: 4px;
    height: 4px;
    border: solid 4px;
    border-radius: 4px;
    margin-right: 4px;
  }

  .status-icon {
    color: inherit;
    margin-right: 4px;
  }
}

.button-light {
    background: white;
    position: relative;
    display: flex;
    flex-direction: row;
    align-items: center;
    height: 26px;
    padding: 0;
    box-shadow: 1px 2px 4px 1px rgba(0, 0, 0, 0.2), 0 0 1px 1px rgba(0, 0, 0, 1);
    padding: 4px 8px;
    gap: 2px;
    width: fit-content;

    @include themify() {
      color: themed('body-text-color')
    }

    &.highlight {
      background: var(--button-color);
      color: var(--button-text-color);

      svg {
        margin-right: 5px;
      }
    }

    &:hover {
      background: rgba(var(--color-primary-100-rgb), 0.5);
    }

    &.delete:not([disabled]) {
      @include themify() {
        background: themed('button-delete');
        color: themed('button-delete-text');
      }
    }

    &.delete[disabled] {
      border: none;
    }

    &.delete:hover:not([disabled]) {
      @include themify() {
        background: $color-red-300;
        color: themed('button-delete-text');
      }
    }
  }
</style>
